/** Shared contact identity helpers for duplicate detection and CSV import. */

export const CONTACT_EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

export const normalizeContactName = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ')

export const normalizeContactEmail = (value: string): string => value.trim().toLowerCase()

export const normalizeContactPhoneDigits = (value: string): string => value.replace(/\D/g, '')

/**
 * Digits used for phone column sort. Matches display: strip non-digits, then drop a
 * leading US country code `1` on 11-digit values so `1225…` and `(225) …` order together.
 * Does not change {@link normalizeContactPhoneDigits} (CSV / duplicate identity).
 */
export const contactPhoneDigitsForSort = (value: string): string => {
  const digits = normalizeContactPhoneDigits(value)
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1)
  }
  return digits
}

/** Sort comparator: order by normalized digits so formatting differences group together. */
export const compareContactPhoneForSort = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  const da = contactPhoneDigitsForSort(a ?? '')
  const db = contactPhoneDigitsForSort(b ?? '')
  if (!da && !db) return 0
  if (!da) return 1
  if (!db) return -1
  // Lexicographic digit order (not numeric): keeps leading zeros and makes
  // ascending/descending predictable for mixed-length numbers.
  return da < db ? -1 : da > db ? 1 : 0
}

/**
 * Title-case a contact name (first letter of each word uppercase, rest lowercase).
 * Handles multi-part names: "john smith" → "John Smith", "MARY-ANN O'BRIEN" → "Mary-Ann O'Brien".
 */
export const formatContactDisplayName = (value: string): string => {
  const trimmed = value.trim().replace(/\s+/g, ' ')
  if (!trimmed) return ''

  const capitalizeToken = (token: string): string => {
    if (!token) return token
    return token
      .split(/([-'])/)
      .map((part) => {
        if (part === '-' || part === "'") return part
        if (!part) return part
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      })
      .join('')
  }

  return trimmed.split(' ').map(capitalizeToken).join(' ')
}

export const isValidContactEmail = (value: string): boolean => {
  const trimmed = value.trim()
  return trimmed.length > 0 && CONTACT_EMAIL_PATTERN.test(trimmed)
}

/** Primary and/or secondary email fields on a contact. */
export type ContactEmailFields = {
  email?: string | null
  secondaryEmail?: string | null
}

/** Unique normalized emails from a contact (primary then secondary). */
export const contactNormalizedEmails = (contact: ContactEmailFields): string[] => {
  const out: string[] = []
  for (const raw of [contact.email, contact.secondaryEmail]) {
    if (typeof raw !== 'string' || !raw.trim()) continue
    const normalized = normalizeContactEmail(raw)
    if (normalized && !out.includes(normalized)) out.push(normalized)
  }
  return out
}

/** True when `email` matches the contact's primary or secondary email. */
export const contactMatchesEmail = (
  contact: ContactEmailFields,
  email: string,
): boolean => {
  const normalized = normalizeContactEmail(email)
  if (!normalized) return false
  return contactNormalizedEmails(contact).includes(normalized)
}
